import React from 'react';
import {
  Box,
  Paper,
  Typography,
  Switch,
  Grid,
  useTheme,
} from '@mui/material';
import {
  DarkMode as DarkModeIcon,
  LightMode as LightModeIcon,
  Shuffle as ShuffleIcon,
  FormatListNumbered as SequentialIcon,
  ErrorOutline as WrongFirstIcon,
  Assessment as StatsIcon,
  Quiz as QuizIcon,
  Score as ScoreIcon
} from '@mui/icons-material';
import { useApp } from '../context/AppContext';
import { QuizModeCard, StatCard } from '../components/settings/SettingsCards';

const SettingsPage = () => {
  const theme = useTheme();
  const { 
    darkMode, 
    setDarkMode,
    quizMode,
    setQuizMode,
    showStatistics,
    setShowStatistics,
    wrongAnswers,
    terms
  } = useApp();

  // 학습 통계 계산
  const totalTerms = terms?.length || 0;
  const totalWrongAnswers = wrongAnswers?.length || 0;
  const correctRate = totalTerms > 0 ? ((totalTerms - totalWrongAnswers) / totalTerms * 100).toFixed(1) : 0;

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: '800px',
        mx: 'auto',
        px: { xs: 2, sm: 3, md: 4 },
        py: { xs: 3, sm: 4 },
      }}
    >
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, sm: 3 },
          borderRadius: 3,
          backgroundColor: 'background.paper',
          backdropFilter: 'blur(10px)',
          boxShadow: 3,
          border: '1px solid',
          borderColor: 'divider',
          overflow: 'hidden',
          position: 'relative',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '4px',
            background: 'linear-gradient(90deg, primary.main, secondary.main)',
          }
        }}
      >
        <Typography variant="h5" component="h1" gutterBottom fontWeight={700}>
          설정
        </Typography>

        <Grid container spacing={3}>
          {/* 다크모드 설정 */}
          <Grid item xs={12}>
            <Paper 
              elevation={1} 
              sx={{ 
                p: 2, 
                display: 'flex', 
                alignItems: 'center',
                justifyContent: 'space-between',
                bgcolor: theme.palette.mode === 'dark' 
                  ? theme.palette.grey[800] 
                  : theme.palette.grey[50],
                border: `1px solid ${theme.palette.divider}`
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                {darkMode ? 
                  <DarkModeIcon sx={{ mr: 2, color: theme.palette.primary.main, fontSize: 28 }} /> :
                  <LightModeIcon sx={{ mr: 2, color: theme.palette.warning.main, fontSize: 28 }} />
                }
                <Box>
                  <Typography variant="h6">
                    {darkMode ? '다크 모드' : '라이트 모드'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    화면 테마를 변경합니다
                  </Typography>
                </Box>
              </Box>
              <Switch
                checked={darkMode}
                onChange={(e) => setDarkMode(e.target.checked)}
                color="primary"
              />
            </Paper>
          </Grid>

          {/* 문제 출제 방식 */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>
              문제 출제 방식
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <QuizModeCard
                  value="random"
                  icon={<ShuffleIcon />}
                  title="랜덤 모드"
                  description="문제가 무작위 순서로 출제됩니다"
                  selected={quizMode === 'random'}
                  onClick={setQuizMode}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <QuizModeCard
                  value="sequential"
                  icon={<SequentialIcon />}
                  title="순차 모드"
                  description="문제가 순서대로 출제됩니다"
                  selected={quizMode === 'sequential'}
                  onClick={setQuizMode}
                />
              </Grid>
            </Grid>
          </Grid>

          {/* 학습 통계 */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
              <StatsIcon sx={{ mr: 1 }} />
              학습 통계
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <StatCard
                  title="총 문제 수"
                  value={totalTerms}
                  icon={<QuizIcon />}
                  color="primary"
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <StatCard
                  title="정답률"
                  value={`${correctRate}%`}
                  icon={<ScoreIcon />}
                  color="success"
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <StatCard
                  title="오답 수"
                  value={totalWrongAnswers}
                  icon={<WrongFirstIcon />}
                  color="error"
                />
              </Grid>
            </Grid>
          </Grid>

          {/* 통계 표시 설정 */}
          <Grid item xs={12}>
            <Paper 
              elevation={1} 
              sx={{ 
                p: 2, 
                display: 'flex', 
                alignItems: 'center',
                justifyContent: 'space-between',
                bgcolor: theme.palette.mode === 'dark' 
                  ? theme.palette.grey[800] 
                  : theme.palette.grey[50],
                border: `1px solid ${theme.palette.divider}`
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <StatsIcon sx={{ mr: 2, color: theme.palette.primary.main, fontSize: 28 }} />
                <Box>
                  <Typography variant="h6">
                    통계 표시
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    학습 통계 표시 여부를 설정합니다
                  </Typography>
                </Box>
              </Box>
              <Switch
                checked={showStatistics}
                onChange={(e) => setShowStatistics(e.target.checked)}
                color="primary"
              />
            </Paper>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
};

export default SettingsPage; 